import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { add, format, startOfToday } from "date-fns";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { IoMdAddCircleOutline } from "react-icons/io";
import AllyService from "../../../services/ally.service";
import { useGlobal } from "../../../context/global";
import { useAlly } from "../../../context/ally";
import CalendarDual from "../../common/CalendarDual";
import { AllyReservationItem, AllyReservations } from "../../../types/common.types";

// "Mis reservas" del perfil del aliado. Igual que la pestaña Payments de vendedores: al elegir un día
// (o un rango) aparecen solo las reservas que tienen un tour en esas fechas, cada una completa, y
// con "+" se le agrega otro tour a ESA misma reserva (mismo grupo y mismos pasajeros) en vez de
// volver a cargar a los pasajeros.

const clp = (n: number) => `$${Math.round(n).toLocaleString('es-CL')}`
const ddmm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`

export default function Reservations() {
    const { setIsLoading } = useGlobal()
    const { setNewGroup, setNewGroupReservationData } = useAlly()
    const [day, setDay] = useState<Date>(startOfToday())
    const [endDay, setEndDay] = useState<Date | undefined>()
    const [isCalendarOpen, setIsCalendarOpen] = useState(false)
    const [data, setData] = useState<AllyReservations>()
    const from = format(day, 'yyyy-MM-dd')
    const to = format(endDay || day, 'yyyy-MM-dd')

    const load = useCallback(async () => {
        setIsLoading(true)
        try {
            const res = await AllyService.listMyReservations(from, to)
            setData(res.data)
        } catch (error) {
            console.log(error)
            setData(undefined)
        } finally {
            setIsLoading(false)
        }
    }, [from, to, setIsLoading])

    useEffect(() => { load() }, [load])

    function moveDay(days: number) {
        setDay(add(day, { days }))
        setEndDay(undefined)
    }

    // Abre el mismo flujo que "crear grupo" (elegir tour y fecha → precio → voucher), pero con los
    // pasajeros y el grupo de esta reserva: el tour nuevo queda dentro de ella.
    function addTour(item: AllyReservationItem) {
        if (!item.group_id) return
        setNewGroupReservationData({ group_id: item.group_id })
        setNewGroup(item.passengers)
    }

    return (
        <Main>
            <Filter>
                <button type="button" aria-label="Día anterior" onClick={() => moveDay(-1)}><IoIosArrowBack /></button>
                <div className="date">
                    <button type="button" className="open-calendar" aria-label="Elegir fechas" onClick={() => setIsCalendarOpen(!isCalendarOpen)}></button>
                    <span>{format(day, 'dd/MM/yyyy')}{endDay ? ` - ${format(endDay, 'dd/MM/yyyy')}` : ''}</span>
                    <div className={`calendar ${isCalendarOpen ? 'open' : ''}`}>
                        <CalendarDual
                            endDay={endDay}
                            setEndDay={setEndDay}
                            selectedDay={day}
                            setSelectedDay={setDay}
                            selectPastDays
                        />
                    </div>
                </div>
                <button type="button" aria-label="Día siguiente" onClick={() => moveDay(1)}><IoIosArrowForward /></button>
            </Filter>

            {data && (
                <div className="totals">
                    <div><span>Tours</span><strong>{data.totals.reservations}</strong></div>
                    <div><span>Pasajeros</span><strong>{data.totals.passengers}</strong></div>
                    <div><span>Total (costo + entrada)</span><strong>{clp(data.totals.total)}</strong></div>
                </div>
            )}

            {data && data.items.length === 0 && <p className="empty">No tienes reservas con tour en estas fechas.</p>}

            {data && data.items.length > 0 && (
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Pasajeros</th>
                                <th>Tours de la reserva</th>
                                <th>Total reserva</th>
                                <th>Agregar tour</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map(item => {
                                const key = item.group_id || item.reservation_id || ''
                                // Un tour por fila aunque vaya más de un pasajero ("Valle de la Luna · 2 pax").
                                const tours = new Map<string, { date: string, tour: string, pax: number, in_range: boolean }>()
                                item.reservations.forEach(r => {
                                    const k = `${r.reservation_date}|${r.tour_id}`
                                    const t = tours.get(k) || { date: r.reservation_date, tour: r.tour_id, pax: 0, in_range: r.in_range }
                                    t.pax += 1
                                    tours.set(k, t)
                                })
                                const total = item.reservations.reduce((acc, r) => acc + r.price + r.tickets, 0)
                                return (
                                    <tr key={key}>
                                        <td>{item.first_date_in_range ? ddmm(item.first_date_in_range) : ''}</td>
                                        <td>{item.passengers.map(p => p.name).join(', ')}</td>
                                        <td>
                                            {[...tours.values()].map(t => (
                                                <div key={`${t.date}${t.tour}`} className={t.in_range ? '' : 'out-of-range'}>
                                                    {ddmm(t.date)} · {t.tour}{t.pax > 1 ? ` · ${t.pax} pax` : ''}
                                                </div>
                                            ))}
                                        </td>
                                        <td>{clp(total)}</td>
                                        <td>
                                            {item.group_id && (
                                                <button
                                                    type="button"
                                                    className="add"
                                                    title="Agregar otro tour a esta reserva"
                                                    aria-label={`Agregar tour a la reserva de ${item.passengers.map(p => p.name).join(', ')}`}
                                                    onClick={() => addTour(item)}
                                                >
                                                    <IoMdAddCircleOutline />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </Main>
    )
}

const Main = styled.main`
display: flex;
flex-direction: column;
gap: 15px;
padding-bottom: 20px;
.totals {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    > div {
        flex: 1 1 120px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 10px 12px;
        border-radius: 8px;
        background-color: ${props => props.theme.whiteBone};
        span { font-size: 12px; color: ${props => props.theme.gray}; }
        strong { font-size: 18px; font-weight: 600; }
    }
}
.empty { color: ${props => props.theme.gray}; text-align: center; }
.table-wrapper { width: 100%; overflow-x: auto; }
table {
    width: 100%;
    min-width: 560px;
    border-collapse: collapse;
    font-size: 13px;
    th { font-weight: 500; text-align: left; padding: 8px; border-bottom: 1px solid ${props => props.theme.gray}; }
    td { padding: 8px; border-bottom: 1px solid ${props => props.theme.whiteBone}; vertical-align: top; }
    .out-of-range { color: ${props => props.theme.gray}; }
    button.add {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        color: #fff;
        background-color: ${props => props.theme.black};
        border: none;
        border-radius: 6px;
        cursor: pointer;
        transition: transform .2s ease;
        &:active { transform: scale(0.9); }
    }
}
`

const Filter = styled.div`
width: 100%;
padding: 20px 0 5px;
display: flex;
align-items: center;
justify-content: space-around;
.date {
    flex: 1;
    max-width: 350px;
    height: 44px;
    margin: 0 10px;
    position: relative;
    z-index: 10;
    > button.open-calendar {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        border: none;
        cursor: pointer;
        background-color: ${props => props.theme.blueLight};
    }
    > span {
        color: #fff;
        position: absolute;
        white-space: nowrap;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        pointer-events: none;
    }
    > .calendar {
        display: none;
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        &.open { display: initial; }
    }
}
> button {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    cursor: pointer;
    background-color: ${props => props.theme.white};
    box-shadow: 0 5px 3px ${props => props.theme.blackTransparent};
}
`
